import { AnimatePresence, motion } from "framer-motion";
import { Command, Keyboard, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const shortcuts = [
  { keys: "⌘ K", label: "Open command palette" },
  { keys: "⌘ F", label: "Search workspace" },
  { keys: "?", label: "Show keyboard shortcuts" },
  { keys: "Esc", label: "Close overlays" },
  { keys: "N", label: "Open add subscription from inventory" },
  { keys: "G D", label: "Go to dashboard" },
  { keys: "G S", label: "Go to subscriptions" },
];

const isTypingTarget = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  return Boolean(element?.closest("input, textarea, select, [contenteditable='true']"));
};

export const ShortcutHelpModal = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let awaitingGoTo = false;
    let goToTimer: number | null = null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (event.key === "?") {
        event.preventDefault();
        setOpen(true);
      }
      if (key === "n") {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent("open-add-service"));
      }
      if (key === "g") {
        awaitingGoTo = true;
        if (goToTimer) window.clearTimeout(goToTimer);
        goToTimer = window.setTimeout(() => {
          awaitingGoTo = false;
        }, 900);
        return;
      }
      if (awaitingGoTo && key === "d") {
        awaitingGoTo = false;
        navigate("/dashboard");
      }
      if (awaitingGoTo && key === "s") {
        awaitingGoTo = false;
        navigate("/subscriptions");
      }
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      if (goToTimer) window.clearTimeout(goToTimer);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [navigate]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            className="fixed left-1/2 top-1/2 z-[100] w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-orizonte-border bg-orizonte-surface p-4 sm:p-5 shadow-2xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-xl border border-orizonte-lime/20 bg-orizonte-lime/10 p-2 text-orizonte-lime">
                  <Keyboard className="h-4 w-4" />
                </div>
                <h2 className="text-base font-semibold text-orizonte-text">Shortcuts</h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1.5 text-orizonte-textMuted transition-colors hover:bg-white/5 hover:text-white"
                aria-label="Close shortcuts"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {shortcuts.map((shortcut) => (
                <div key={shortcut.keys} className="flex items-center justify-between gap-3 sm:gap-4 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 sm:py-2.5">
                  <span className="text-xs sm:text-sm text-orizonte-textMuted">{shortcut.label}</span>
                  <span className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-black/20 px-2 py-1 text-[11px] sm:text-xs font-semibold text-orizonte-text shrink-0">
                    {shortcut.keys.includes("⌘") && <Command className="h-3 w-3" />}
                    {shortcut.keys.replace("⌘ ", "")}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
